import { useEffect, useState, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
// CHART POPUP DISABLED — amCharts no longer used here
// import * as am5 from '@amcharts/amcharts5'
// import * as am5xy from '@amcharts/amcharts5/xy'
// import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import solarIcon from '../../../assets/conwx-portfolio/solar.svg'
import windIcon from '../../../assets/conwx-portfolio/wind.svg'
import weatherIcon from '../../../assets/conwx-portfolio/weather.svg'

// Format a number with thousands separators (e.g. 37034 -> "37,034")
const formatNumber = value => Number(value || 0).toLocaleString('en-US')

/**
 * Component that renders enhanced badge markers with data values
 */
export const EnhancedBadgeMarkers = ({ countries }) => {
  const map = useMap()
  const markersRef = useRef([])
  const [currentZoom, setCurrentZoom] = useState(map.getZoom())

  // Listen for zoom changes
  useEffect(() => {
    const handleZoomEnd = () => {
      setCurrentZoom(map.getZoom())
    }

    map.on('zoomend', handleZoomEnd)

    return () => {
      map.off('zoomend', handleZoomEnd)
    }
  }, [map])

  useEffect(() => {
    // Clean up existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    if (!countries || countries.length === 0) return

    // Calculate CSS transform scale based on zoom level
    const baseZoom = 5
    const scale = Math.pow(1.2, currentZoom - baseZoom)
    const constrainedScale = Math.max(0.7, Math.min(2.5, scale))

    countries.forEach((country, idx) => {
      // Skip countries without a resolved center (no matching GeoJSON geometry)
      if (!country.center) return

      // COUNTRY COMPARISON DISABLED — selection always off
      const isSelected = false
      // const isSelected = selectedCountries.some(
      //   selected => selected.code === country.code,
      // )

      // Create enhanced badge div with data
      const badgeDiv = document.createElement('div')
      badgeDiv.style.width = '120px'
      badgeDiv.style.height = '90px'
      badgeDiv.style.backgroundColor = isSelected ? '#2E7D32' : '#368ED8'
      badgeDiv.style.borderRadius = '8px'
      badgeDiv.style.display = 'flex'
      badgeDiv.style.flexDirection = 'column'
      badgeDiv.style.alignItems = 'center'
      badgeDiv.style.justifyContent = 'center'
      badgeDiv.style.color = 'white'
      badgeDiv.style.fontSize = '9px'
      badgeDiv.style.fontWeight = 'normal'
      badgeDiv.style.cursor = 'pointer'
      badgeDiv.style.border = isSelected
        ? '2px solid #4CAF50'
        : '2px solid white'
      badgeDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)'
      badgeDiv.style.padding = '2px 4px'
      badgeDiv.style.transition = 'all 0.2s ease'

      // Scale badge based on zoom level
      badgeDiv.style.transform = `scale(${constrainedScale})`
      badgeDiv.style.transformOrigin = 'center center'

      // Add selection indicator
      if (isSelected) {
        const indicator = document.createElement('div')
        indicator.textContent = '✓'
        indicator.style.position = 'absolute'
        indicator.style.top = '-5px'
        indicator.style.right = '-5px'
        indicator.style.backgroundColor = '#4CAF50'
        indicator.style.borderRadius = '50%'
        const indicatorSize = 16
        indicator.style.width = `${indicatorSize}px`
        indicator.style.height = `${indicatorSize}px`
        indicator.style.display = 'flex'
        indicator.style.alignItems = 'center'
        indicator.style.justifyContent = 'center'
        indicator.style.fontSize = '10px'
        indicator.style.color = 'white'
        badgeDiv.style.position = 'relative'
        badgeDiv.appendChild(indicator)
      }

      // Add country code
      const countryCode = document.createElement('div')
      countryCode.textContent = country.code
      countryCode.style.fontSize = '18px'
      countryCode.style.fontWeight = '600'
      countryCode.style.marginBottom = '2px'
      badgeDiv.appendChild(countryCode)

      // Create style element for badge styles
      const style = document.createElement('style')
      style.textContent = `
        .badge-icon {
          width: 16px;
          height: 16px;
          display: inline-block;
          filter: brightness(0) invert(1);
          margin-right: 3px;
          flex-shrink: 0;
        }
        .badge-row {
          opacity: 0.95;
          margin-bottom: 3px;
          line-height: 1.4;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .badge-value {
          font-size: 13px;
          line-height: 16px;
        }
        .badge-unit {
          font-size: 8px;
          line-height: 16px;
        }
        .weather-row {
          opacity: 0.9;
          margin-bottom: 0px;
          line-height: 1.4;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .weather-value {
          font-size: 13px;
          line-height: 16px;
          position: relative;
          top: 1px;
        }
        .client-tooltip {
          position: absolute;
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 12px;
          border-radius: 6px;
          font-size: 12px;
          line-height: 1.6;
          min-width: 200px;
          max-width: 300px;
          z-index: 10000;
          pointer-events: none;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        .client-tooltip::before {
          content: '';
          position: absolute;
          left: -8px;
          top: 20px;
          width: 0;
          height: 0;
          border-top: 8px solid transparent;
          border-bottom: 8px solid transparent;
          border-right: 8px solid rgba(0, 0, 0, 0.9);
        }
        .client-tooltip-title {
          font-weight: 600;
          margin-bottom: 8px;
          font-size: 13px;
          border-bottom: 1px solid rgba(255,255,255,0.2);
          padding-bottom: 6px;
        }
        .client-tooltip-section {
          margin-bottom: 6px;
        }
        .client-tooltip-label {
          font-weight: 600;
          color: #4CAF50;
          margin-bottom: 2px;
        }
        .client-tooltip-clients {
          color: #e0e0e0;
          font-size: 11px;
        }
      `
      badgeDiv.appendChild(style)

      // Add Solar info
      const solar = document.createElement('div')
      solar.className = 'badge-row'
      solar.innerHTML = `<img src="${solarIcon}" class="badge-icon"/><span class="badge-value">${formatNumber(country.solar?.mw)}</span><span class="badge-unit">MW</span><span class="badge-value">&nbsp;/&nbsp;${formatNumber(country.solar?.sites)}</span>`
      badgeDiv.appendChild(solar)

      // Add Wind info
      const wind = document.createElement('div')
      wind.className = 'badge-row'
      wind.innerHTML = `<img src="${windIcon}" class="badge-icon"/><span class="badge-value">${formatNumber(country.wind?.mw)}</span><span class="badge-unit">MW</span><span class="badge-value">&nbsp;/&nbsp;${formatNumber(country.wind?.sites)}</span>`
      badgeDiv.appendChild(wind)

      // Add Weather locations
      const weather = document.createElement('div')
      weather.className = 'weather-row'
      weather.innerHTML = `<img src="${weatherIcon}" class="badge-icon"/><span class="weather-value">${formatNumber(country.weather?.locations)}</span>`
      badgeDiv.appendChild(weather)

      // Create custom icon
      const icon = L.divIcon({
        className: 'country-badge-marker',
        html: badgeDiv.outerHTML,
        iconSize: [120, 90],
        iconAnchor: [60, 45],
      })

      /* CHART POPUP DISABLED — right-click chart builder hidden for now
      // Create popup content with chart
      const createPopupContent = () => {
        const popupDiv = document.createElement('div')
        popupDiv.style.width = '300px'
        popupDiv.style.height = '200px'

        const header = document.createElement('div')
        header.textContent = country.name
        header.style.fontSize = '16px'
        header.style.fontWeight = 'bold'
        header.style.marginBottom = '10px'
        header.style.textAlign = 'center'
        header.style.color = '#368ED8'
        popupDiv.appendChild(header)

        const chartContainer = document.createElement('div')
        const chartId = `popup-chart-${country.code}-${idx}`
        chartContainer.id = chartId
        chartContainer.style.width = '100%'
        chartContainer.style.height = '160px'
        popupDiv.appendChild(chartContainer)

        // Initialize chart
        setTimeout(() => {
          // Clean up any existing charts with the same ID
          am5.array.each(am5.registry.rootElements, function (root) {
            if (root && root.dom && root.dom.id === chartId) {
              root.dispose()
            }
          })

          const root = am5.Root.new(chartId)
          root.setThemes([am5themes_Animated.new(root)])
          root._logo.dispose()
          root.numberFormatter.set('numberFormat', '#')

          const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
              panX: false,
              panY: false,
              wheelX: 'none',
              wheelY: 'none',
            }),
          )

          const xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
              categoryField: 'label',
              renderer: am5xy.AxisRendererX.new(root, {
                minGridDistance: 20,
              }),
            }),
          )

          xAxis.get('renderer').labels.template.setAll({
            fontSize: 10,
          })

          xAxis.data.setAll(country.chartData)

          const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
              renderer: am5xy.AxisRendererY.new(root, {}),
            }),
          )

          yAxis.get('renderer').labels.template.setAll({
            fontSize: 10,
          })

          const series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
              name: 'Value',
              xAxis: xAxis,
              yAxis: yAxis,
              valueYField: 'value',
              categoryXField: 'label',
              tooltip: am5.Tooltip.new(root, {
                labelText: '{categoryX}: {valueY}',
              }),
            }),
          )

          series.columns.template.setAll({
            cornerRadiusTL: 3,
            cornerRadiusTR: 3,
            strokeOpacity: 0,
            fill: am5.color(0x368ed8),
          })

          series.data.setAll(country.chartData)
          series.appear(1000)
          chart.appear(1000, 100)
        }, 100)

        return popupDiv
      }
      */

      // Create marker with both popup and selection functionality
      const marker = L.marker([country.center.lat, country.center.lon], {
        icon: icon,
      }).addTo(map)

      /* HOVER CLIENT POPUP DISABLED — hidden for now
      // Create tooltip element for client information
      const tooltip = document.createElement('div')
      tooltip.className = 'client-tooltip'
      tooltip.style.display = 'none'

      // Build tooltip content
      const tooltipTitle = document.createElement('div')
      tooltipTitle.className = 'client-tooltip-title'
      tooltipTitle.textContent = country.name
      tooltip.appendChild(tooltipTitle)

      // Solar clients
      if (country.solar?.clients && country.solar.clients.length > 0) {
        const solarSection = document.createElement('div')
        solarSection.className = 'client-tooltip-section'
        const solarLabel = document.createElement('div')
        solarLabel.className = 'client-tooltip-label'
        solarLabel.textContent = 'Solar:'
        const solarClients = document.createElement('div')
        solarClients.className = 'client-tooltip-clients'
        solarClients.textContent = country.solar.clients.join(', ')
        solarSection.appendChild(solarLabel)
        solarSection.appendChild(solarClients)
        tooltip.appendChild(solarSection)
      }

      // Wind clients
      if (country.wind?.clients && country.wind.clients.length > 0) {
        const windSection = document.createElement('div')
        windSection.className = 'client-tooltip-section'
        const windLabel = document.createElement('div')
        windLabel.className = 'client-tooltip-label'
        windLabel.textContent = 'Wind:'
        const windClients = document.createElement('div')
        windClients.className = 'client-tooltip-clients'
        windClients.textContent = country.wind.clients.join(', ')
        windSection.appendChild(windLabel)
        windSection.appendChild(windClients)
        tooltip.appendChild(windSection)
      }

      // Weather clients
      if (country.weather?.clients && country.weather.clients.length > 0) {
        const weatherSection = document.createElement('div')
        weatherSection.className = 'client-tooltip-section'
        const weatherLabel = document.createElement('div')
        weatherLabel.className = 'client-tooltip-label'
        weatherLabel.textContent = 'Weather:'
        const weatherClients = document.createElement('div')
        weatherClients.className = 'client-tooltip-clients'
        weatherClients.textContent = country.weather.clients.join(', ')
        weatherSection.appendChild(weatherLabel)
        weatherSection.appendChild(weatherClients)
        tooltip.appendChild(weatherSection)
      }

      document.body.appendChild(tooltip)

      // Handle marker hover for tooltip
      marker.on('mouseover', e => {
        const markerElement = e.target.getElement()
        if (markerElement) {
          const rect = markerElement.getBoundingClientRect()
          tooltip.style.display = 'block'
          tooltip.style.left = `${rect.right + 10}px`
          tooltip.style.top = `${rect.top}px`
        }
      })

      marker.on('mouseout', () => {
        tooltip.style.display = 'none'
      })
      */

      /* CLICK INTERACTIONS DISABLED — selection & chart popup hidden for now
      // Handle marker click for selection
      marker.on('click', e => {
        e.originalEvent.stopPropagation()
        onCountrySelect(country)
      })

      // Keep popup functionality with right-click or long press
      marker.bindPopup(() => createPopupContent(), {
        maxWidth: 320,
        className: 'country-chart-popup',
      })

      // Show popup on right-click
      marker.on('contextmenu', e => {
        e.originalEvent.preventDefault()
        marker.openPopup()
      })
      */

      markersRef.current.push(marker)
    })

    // Cleanup function to remove tooltips
    return () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
      /* HOVER CLIENT POPUP DISABLED — nothing to clean up
      // Remove all tooltips from DOM
      document.querySelectorAll('.client-tooltip').forEach(tooltip => {
        tooltip.remove()
      })
      */
    }
  }, [countries, map, currentZoom])

  return null
}
